/**
 * Container for a single Business Office comment row.
 *
 * Owns the local `boStatus` and `writeBackComment` state, wires the
 * EV-write-back chip / autosize textarea / analytics hooks, and
 * mounts the trail screenshot modal when invoked from the eye icon, and
 * surfaces template-validator QC issues below the write-back note when present.
 *
 * Stateless presentational pieces live alongside in this folder.
 *
 * @module busniessOffice/components/singleComment/SingleComment
 */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import ImageViewerWithBadgesModal from "../../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import { useBusniessOfficeNotesContext } from "../../../../../../context/busniessOfficeNotesContext";
import { shouldShowWritebackQcNotesForAuthStatus } from "../../../../../../data-model/healthFacility";
import { ModalId } from "../../../../../../enums/modalId";
import { UserGeneralInfoContextKeys } from "../../../../../../enums/userGeneralInfoContextKeys";
import { AppDispatch, RootState } from "../../../../../../redux/store/store";
import { UserStore } from "../../../../../../store/UserGeneralInfoStore";
import { handleDisplayWriteBackTrailScreenshot } from "../../actionHandlers/displayWriteBackScreenshot";
import { useAutoGrowTextarea } from "./hooks/useAutoGrowTextarea";
import { useBoCommentAnalytics } from "./hooks/useBoCommentAnalytics";
import { useBoStatusOptions } from "./hooks/useBoStatusOptions";
import { useEvWriteBackStatus } from "./hooks/useEvWriteBackStatus";
import OriginalCommentPanel from "./OriginalCommentPanel";
import { SingleCommentProps } from "./types";
import WriteBackHeader from "./WriteBackHeader";
import WriteBackPanel from "./WriteBackPanel";
import WritebackQcBelowNote from "./WritebackQcBelowNote";
import WriteBackTextarea from "./WriteBackTextarea";

const SingleComment = (props: SingleCommentProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const [writeBackComment, setWriteBackComment] = useState(
    props.writeBackComment,
  );
  const [fcHoldCommentDraft, setFcHoldCommentDraft] = useState(
    props.fcHoldComment ?? "",
  );
  const [boStatus, setBoStatus] = useState(props.boStatus);
  const boStatusRef = useRef(boStatus);
  const oldDataForLogEvent = useRef<string>(props.writeBackComment);

  const { data: boOptions, loading: boOptionsLoading } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );

  const boStatusOptions = useBoStatusOptions(boOptions);
  const { label: evStatusLabel, color: evStatusColor } = useEvWriteBackStatus(
    props.writeStatus,
  );
  const {
    textareaRef,
    writeBackContainerRef,
    commentsContainerRef,
    recompute: recomputeTextareaHeight,
  } = useAutoGrowTextarea();
  const analytics = useBoCommentAnalytics(
    props.generalOrderDetails,
    props.authOnFile,
    props.index,
  );
  const { writebackQcByReportId } = useBusniessOfficeNotesContext();
  const showWritebackQc = shouldShowWritebackQcNotesForAuthStatus(
    singleHealthFacilityDetails?.feature_config,
    props.authOnFile?.auth_status,
  );
  const writebackQcIssues = showWritebackQc
    ? (writebackQcByReportId[props.id] ?? [])
    : [];

  useEffect(() => {
    setBoStatus(props.boStatus);
    boStatusRef.current = props.boStatus;
  }, [props.boStatus]);

  useEffect(() => {
    boStatusRef.current = boStatus;
  }, [boStatus]);

  useEffect(() => {
    setWriteBackComment(props.writeBackComment);
  }, [props.writeBackComment]);

  useEffect(() => {
    setFcHoldCommentDraft(props.fcHoldComment ?? "");
  }, [props.fcHoldComment]);

  useEffect(() => {
    recomputeTextareaHeight();
  }, [writeBackComment, props.showWriteBackNote, recomputeTextareaHeight]);

  useEffect(() => {
    if (openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL);
    }
  }, [openedModalId]);

  const orderIdForJCodes = props.generalOrderDetails?.order_id ?? "";
  const jCodesFromContext = UserStore.getItem(
    `${UserGeneralInfoContextKeys.J_CODES}_${orderIdForJCodes}`,
  );
  const jCodes = jCodesFromContext?.join(",") ?? "";

  const handleBoStatusSelect = (badge: string) => {
    analytics.logBoStatusSelected({
      currentValue: boStatusRef.current ?? "",
      newValue: badge,
    });
    setBoStatus(badge);
    props.onBoStatusChange({ id: props.id, status: badge });
  };

  const handleBoStatusDefaultClick = () => {
    analytics.logBoStatusDefaultClick({
      currentValue: boStatusRef.current ?? "",
    });
  };

  const handleEvCompletedEyeClick = () => {
    handleDisplayWriteBackTrailScreenshot(
      props.generalOrderDetails?.order_id ?? "",
      dispatch,
      authStatusOptions ?? [],
    );
  };

  const handleWriteBackContainerClick = () => {
    analytics.logWriteBackContainerClicked({ jCodes });
  };

  const handleWriteBackChange = (nextValue: string) => {
    setWriteBackComment(nextValue);
    props.onWriteBackCommentChange({ id: props.id, comment: nextValue });
    recomputeTextareaHeight();
    analytics.logWriteBackEdited({
      newValue: nextValue,
      oldValue: oldDataForLogEvent.current,
      jCodes,
    });
  };

  return (
    <div className="single-comment--container flex gap-5">
      <OriginalCommentPanel
        originalComment={props.originalComment}
        containerRef={commentsContainerRef}
      />
      <WriteBackPanel
        id={props.id}
        showWriteBackNote={!!props.showWriteBackNote}
        containerRef={writeBackContainerRef}
      >
        <WriteBackHeader
          boStatus={boStatus}
          boStatusOptions={boStatusOptions}
          boOptionsLoading={boOptionsLoading}
          evStatusLabel={evStatusLabel}
          evStatusColor={evStatusColor}
          onBoStatusSelect={handleBoStatusSelect}
          onBoStatusDefaultClick={handleBoStatusDefaultClick}
          onEvCompletedEyeClick={handleEvCompletedEyeClick}
          errorMessage={
            props.generalOrderDetails?.status?.meta?.ev_write_back_status_error
              ?.error_message
          }
        />
        {props.fcHoldComment !== undefined && (
          <div className="border-b border-dashed border-primaryGray-14 px-4 py-2 text-h12">
            <label className="font-bold">Comment</label>
            <textarea
              value={fcHoldCommentDraft}
              onChange={(e) => {
                setFcHoldCommentDraft(e.target.value);
                props.onFcHoldCommentChange?.(e.target.value);
              }}
              placeholder="RISA Working (date) is added automatically on first Trigger RPA"
              rows={2}
              className="mt-1 w-full resize-y bg-transparent text-h12 outline-none"
            />
          </div>
        )}
        <WriteBackTextarea
          id={props.id}
          value={writeBackComment}
          onChange={handleWriteBackChange}
          onContainerClick={handleWriteBackContainerClick}
          textareaRef={textareaRef}
        />
        <WritebackQcBelowNote reportId={props.id} issues={writebackQcIssues} />
      </WriteBackPanel>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
        />
      )}
    </div>
  );
};

export default SingleComment;
